import { type ClassValue, clsx } from 'clsx';
import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const isPhoneValid = (phone: string) => {
  try {
    return isValidPhoneNumber(phone);
  } catch (error) {
    return false;
  }
};

/**
 * Check if the user is a Creatr team member (email ends with @getcreatr.com)
 */
export const isCreatrTeamMember = (
  email: string | null | undefined
): boolean => {
  if (!email) return false;
  return email.toLowerCase().endsWith('@getcreatr.com');
};

/**
 * Format a message timestamp for display during admin impersonation
 * @param date - The date to format (string or Date object)
 * @returns Formatted string like "Jan 29, 2:34 PM"
 */
export function formatMessageTimestamp(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date;

  // Check if date is valid
  if (!isValid(d)) {
    return '';
  }

  // Format as "Jan 29, 2:34 PM"
  return format(d, 'MMM d, h:mm a');
}

/**
 * Format a message timestamp as relative time (e.g., "Just now", "5 minutes ago")
 * @param date - The date to format (string or Date object)
 * @returns Formatted relative time string
 */
export function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date;

  // Check if date is valid
  if (!isValid(d)) {
    return '';
  }

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000);

  // Less than 30 seconds ago
  if (diffInSeconds < 30) {
    return 'Just now';
  }

  // Use date-fns formatDistanceToNow for other cases and remove "about"
  const relativeTime = formatDistanceToNow(d, { addSuffix: true });
  return relativeTime.replace(/^about\s+/i, '');
}

/**
 * Format a full timestamp for tooltip display
 * @param date - The date to format (string or Date object)
 * @returns Formatted string like "6:07 AM, Feb 5, 2026"
 */
export function formatFullTimestamp(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date;

  // Check if date is valid
  if (!isValid(d)) {
    return '';
  }

  // Format as "6:07 AM, Feb 5, 2026"
  return format(d, 'h:mm a, MMM d, yyyy');
}

export { isPhoneValid };
